"use client";

import { cn } from "@/lib/cn";
import { Link, usePathname } from "@/i18n/navigation";
import type { ComponentType, ReactNode } from "react";

export interface BottomNavItem {
  /** Ruta sin prefijo de locale, ej. "/app/bookings". */
  href: string;
  label: ReactNode;
  icon: ComponentType<{ size?: number }>;
  /** Match exacto en vez de prefijo (útil para el home de la sección). */
  exact?: boolean;
}

/**
 * BottomNav — navegación inferior fija para las PWA de cliente y terapeuta.
 * Mobile-first: se muestra sólo hasta `md`; en desktop las superficies usan su
 * propia navegación lateral/superior. Respeta el safe-area del notch.
 *
 * Uso: cada superficie define sus items y los pasa acá.
 */
export function BottomNav({ items }: { items: BottomNavItem[] }) {
  const pathname = usePathname(); // ya viene SIN prefijo de locale

  return (
    <nav
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 md:hidden",
        "border-t border-sand-300 bg-bone-50/95 backdrop-blur",
        "pb-[env(safe-area-inset-bottom)]",
      )}
      aria-label="Navegación principal"
    >
      <ul className="mx-auto flex max-w-md items-stretch justify-around px-2">
        {items.map((it) => {
          const active = it.exact
            ? pathname === it.href
            : pathname === it.href || pathname.startsWith(it.href + "/");
          const Icon = it.icon;
          return (
            <li key={it.href} className="flex-1">
              <Link
                href={it.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex flex-col items-center gap-1 py-2.5 text-[0.7rem] font-medium transition-colors",
                  active ? "text-forest" : "text-ink-muted hover:text-ink-soft",
                )}
              >
                <Icon size={22} />
                <span>{it.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
